<script setup>
import { ref, onMounted, computed } from 'vue';
import StripePayment from '../../components/StripePayments.vue';
import appIcon from '@/assets/images/LuckyFridayBrightIcon.png';
import { useRouter, useRoute } from 'vue-router';
import ProgressBar from '../../components/ProgressBar.vue';
import { Capacitor } from '@capacitor/core';
import {
  RevenueCatUI,
  PAYWALL_RESULT,
} from '@revenuecat/purchases-capacitor-ui';
import { useMyStore } from '../../store';

const selectedPlan = ref('annual');
const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const router = useRouter();
const route = useRoute();
const isExistingUser = route.query.isExistingUser;
const subscribed = ref(false);
const isIosApp = ref(false);
const store = useMyStore();
const appliedDiscount = ref(null);

const handleDiscountApplied = (discount) => {
  appliedDiscount.value = discount;
};

const annualOriginal = 99.99;
const monthlyOriginal = 12.99;

const annualDiscounted = computed(() => {
  if (!appliedDiscount.value?.percentOff) return null;
  return (
    annualOriginal *
    (1 - appliedDiscount.value.percentOff / 100)
  ).toFixed(2);
});

const monthlyDiscounted = computed(() => {
  if (!appliedDiscount.value?.percentOff) return null;
  return (
    monthlyOriginal *
    (1 - appliedDiscount.value.percentOff / 100)
  ).toFixed(2);
});

const annualMonthlyEquivalent = computed(() => {
  const price = annualDiscounted.value ?? annualOriginal;
  return (price / 12).toFixed(2);
});

const selectPlan = (planType) => {
  selectedPlan.value = planType;
};

const handlePaymentSuccess = async () => {
  subscribed.value = true;
};

const goToCategorySelection = () => {
  router.push({ name: 'UserCategoryData' });
};

const useFreeVersion = () => {
  if (isExistingUser) {
    router.push({ name: 'UserProfile' });
  } else {
    goToCategorySelection();
  }
};

const goToAccountPage = () => {
  router.push({ name: 'UserProfile' });
};

onMounted(() => {
  setTimeout(async () => {
    if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
      isIosApp.value = true;
      await presentIosPaywall();
      return;
    }
    const mainElement = document.querySelector('.main');
    if (mainElement) mainElement.scrollTo(0, 0);
  }, 100);
});

const presentIosPaywall = async () => {
  try {
    const { result } = await RevenueCatUI.presentPaywall();
    switch (result) {
      case PAYWALL_RESULT.PURCHASED:
      case PAYWALL_RESULT.RESTORED:
        await store.activateIapSubscription();
        subscribed.value = true;
        break;
      default:
        break;
    }
  } catch (e) {
    console.error('Paywall error:', e);
  }
};
</script>

<template>
  <div class="paywall-container">
    <!-- ── SUCCESS STATE ── -->
    <div v-if="subscribed" class="paywall-success">
      <div class="success-icon">✦</div>
      <h2 class="success-title">You're Premium!</h2>
      <p class="success-body">
        <span v-if="isExistingUser"
          >You're now subscribed to the most customizable budgeting app on the
          market.</span
        >
        <span v-else
          >You're now subscribed. Let's get you set up with your first
          categories.</span
        >
      </p>
      <button
        class="paywall-cta"
        @click="isExistingUser ? goToAccountPage() : goToCategorySelection()"
      >
        {{ isExistingUser ? 'Go to Profile' : "Let's go" }}
      </button>
    </div>

    <!-- ── MAIN PAYWALL (web) ── -->
    <div v-else-if="!subscribed && !isIosApp">
      <!-- Hero -->
      <div class="paywall-hero">
        <div class="paywall-hero-orb">
          <img :src="appIcon" alt="Lucky Friday" class="paywall-app-icon" />
        </div>
        <h1 class="paywall-headline">
          Budget faster with<br /><span class="paywall-headline-accent"
            >Lucky Friday Premium</span
          >
        </h1>
      </div>

      <!-- Free / Premium toggle -->
      <div class="paywall-tier-toggle">
        <span
          class="tier-label"
          :class="{ 'tier-inactive': selectedPlan !== 'free' }"
          >Free</span
        >
        <div class="tier-pill">
          <div
            class="tier-pill-knob"
            :class="{ 'knob-right': selectedPlan !== 'free' }"
          ></div>
        </div>
        <span
          class="tier-label tier-premium"
          :class="{ 'tier-inactive': selectedPlan === 'free' }"
          >Premium</span
        >
      </div>

      <!-- Benefits -->
      <ul class="paywall-benefits">
        <li class="benefit-item">
          <span class="benefit-check">✓</span>
          <span>One-Click Cancel Anytime</span>
        </li>
        <li class="benefit-item">
          <span class="benefit-check">✓</span>
          <span>No Ads — Ever</span>
        </li>
        <li class="benefit-item">
          <span class="benefit-check">✓</span>
          <span>Data Privacy: We'll never sell or share your info</span>
        </li>
        <li class="benefit-item">
          <span class="benefit-check">✓</span>
          <span>Securely link unlimited bank accounts</span>
        </li>
        <li class="benefit-item">
          <span class="benefit-check">✓</span>
          <span
            >Link other financial accounts including investments, loans, and
            more</span
          >
        </li>
      </ul>

      <!-- Pricing cards -->
      <div class="paywall-plans">
        <div
          class="plan-card"
          :class="{ 'plan-card-selected': selectedPlan === 'annual' }"
          @click="selectPlan('annual')"
        >
          <div class="plan-save-badge">SAVE 19%</div>
          <div class="plan-name">Yearly</div>
          <!-- <div class="plan-price">
            $99.99<span class="plan-period">/yr</span>
          </div>
          <div class="plan-sub">Only $8.33/mo</div> -->
          <div class="plan-price">
            <template v-if="annualDiscounted">
              <span class="plan-price-original">${{ annualOriginal }}</span>
              ${{ annualDiscounted }}<span class="plan-period">/yr</span>
            </template>
            <template v-else>
              ${{ annualOriginal }}<span class="plan-period">/yr</span>
            </template>
          </div>
          <div class="plan-sub">Only ${{ annualMonthlyEquivalent }}/mo</div>
          <div
            class="plan-radio"
            :class="{ 'plan-radio-selected': selectedPlan === 'annual' }"
          >
            <div v-if="selectedPlan === 'annual'" class="plan-radio-dot"></div>
          </div>
        </div>

        <div
          class="plan-card"
          :class="{ 'plan-card-selected': selectedPlan === 'monthly' }"
          @click="selectPlan('monthly')"
        >
          <div class="plan-name">Monthly</div>
          <!-- <div class="plan-price">
            $12.99<span class="plan-period">/mo</span>
          </div> -->
          <div class="plan-price">
            <template v-if="monthlyDiscounted">
              <span class="plan-price-original">${{ monthlyOriginal }}</span>
              ${{ monthlyDiscounted }}<span class="plan-period">/mo</span>
            </template>
            <template v-else>
              ${{ monthlyOriginal }}<span class="plan-period">/mo</span>
            </template>
          </div>
          <div class="plan-sub">Billed monthly</div>
          <div
            class="plan-radio"
            :class="{ 'plan-radio-selected': selectedPlan === 'monthly' }"
          >
            <div v-if="selectedPlan === 'monthly'" class="plan-radio-dot"></div>
          </div>
        </div>
      </div>

      <!-- Stripe payment -->
      <div class="paywall-stripe">
        <StripePayment
          :amount="amount"
          :publishable-key="publishableKey"
          @payment-success="handlePaymentSuccess"
          @discount-applied="handleDiscountApplied"
          :selectedPlan="selectedPlan"
        />
        <!-- <StripePayment
          :amount="amount"
          :publishable-key="publishableKey"
          @payment-success="handlePaymentSuccess"
          :selectedPlan="selectedPlan"
        /> -->
      </div>

      <!-- Free version -->
      <div class="paywall-footer">
        <button class="paywall-free-btn" @click="useFreeVersion">
          Continue with free version
        </button>
        <div class="paywall-legal-links">
          <a href="#" class="legal-link">Restore Purchases</a>
          <a href="#" class="legal-link">Terms</a>
          <a href="#" class="legal-link">Privacy</a>
        </div>
      </div>
    </div>

    <!-- ── iOS PAYWALL FALLBACK ── -->
    <div v-else-if="!subscribed && isIosApp">
      <div class="paywall-hero">
        <div class="paywall-hero-orb">
          <img :src="appIcon" alt="Lucky Friday" class="paywall-app-icon" />
        </div>
        <h1 class="paywall-headline">
          Budget faster with<br /><span class="paywall-headline-accent"
            >Lucky Friday Premium</span
          >
        </h1>
      </div>

      <ul class="paywall-benefits">
        <li class="benefit-item">
          <span class="benefit-check">✓</span
          ><span>One-Click Cancel Anytime</span>
        </li>
        <li class="benefit-item">
          <span class="benefit-check">✓</span><span>No Ads — Ever</span>
        </li>
        <li class="benefit-item">
          <span class="benefit-check">✓</span
          ><span>Data Privacy: We'll never sell or share your info</span>
        </li>
        <li class="benefit-item">
          <span class="benefit-check">✓</span
          ><span>Securely link unlimited bank accounts</span>
        </li>
        <li class="benefit-item">
          <span class="benefit-check">✓</span
          ><span
            >Link other financial accounts including investments, loans, and
            more</span
          >
        </li>
      </ul>

      <button class="paywall-cta" @click="presentIosPaywall">Continue</button>

      <div class="paywall-footer mt-2">
        <button class="paywall-free-btn" @click="useFreeVersion">
          Continue with free version
        </button>
        <div class="paywall-legal-links">
          <a href="#" class="legal-link">Restore Purchases</a>
          <a href="#" class="legal-link">Terms</a>
          <a href="#" class="legal-link">Privacy</a>
        </div>
      </div>
    </div>

    <ProgressBar
      v-if="!isExistingUser && !subscribed"
      :currentStep="2"
      :steps="5"
    />
  </div>
</template>

<style scoped>
.paywall-container {
  max-width: 480px;
  margin: 0 auto;
  padding: 1.5rem 1rem 2rem;
}

/* ── Hero ── */
.paywall-hero {
  text-align: center;
  margin-bottom: 1.5rem;
}
.paywall-hero-orb {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: var(--teal-50);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  animation: popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
.paywall-app-icon {
  width: 64px;
  height: 64px;
  border-radius: 14px;
}
.paywall-headline {
  font-size: 1.3rem;
  font-weight: 700;
  color: #213547;
  line-height: 1.35;
  letter-spacing: -0.02em;
  margin: 0;
}
.dark-theme .paywall-headline {
  color: white;
}
.paywall-headline-accent {
  color: var(--color-accent-purple);
}

/* ── Tier toggle ── */
.paywall-tier-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 1.25rem;
}
.tier-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #213547;
  transition: color 0.2s;
}
.dark-theme .tier-label {
  color: white;
}
.tier-inactive {
  color: #aaa;
}
.tier-premium {
  color: var(--color-accent-purple);
}
.tier-pill {
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: var(--gradient-primary);
  position: relative;
  cursor: pointer;
}
.tier-pill-knob {
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  top: 3px;
  left: 3px;
  transition: left 0.2s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}
.knob-right {
  left: 23px;
}

/* ── Benefits ── */
.paywall-benefits {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  background: white;
  border-radius: var(--radius-lg);
  border: 1px solid #f0f0f0;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}
.dark-theme .paywall-benefits {
  background: #212e3e;
  border-color: #2a3a4e;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.85rem;
  color: #213547;
  line-height: 1.4;
}
.dark-theme .benefit-item {
  border-bottom-color: #2a3a4e;
  color: #d4d2d2;
}
.benefit-item:last-child {
  border-bottom: none;
}
.benefit-check {
  color: var(--color-accent-teal);
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
  margin-top: 1px;
}

/* ── Plans ── */
.paywall-plans {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 1.5rem;
}

.plan-card {
  position: relative;
  background: white;
  border: 2px solid #e7e7e7;
  border-radius: var(--radius-lg);
  padding: 1rem 0.85rem 0.85rem;
  cursor: pointer;
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    transform 0.15s;
  text-align: left;
}
.dark-theme .plan-card {
  background: #212e3e;
  border-color: #2a3a4e;
}
.plan-card:hover {
  transform: translateY(-1px);
}
.plan-card-selected {
  border-color: var(--color-accent-purple);
  box-shadow: 0 4px 20px rgba(127, 119, 221, 0.2);
}

.plan-save-badge {
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--gradient-primary);
  color: white;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

.plan-name {
  font-size: 0.78rem;
  font-weight: 600;
  color: #888;
  margin-bottom: 4px;
}
.plan-card-selected .plan-name {
  color: var(--color-accent-purple);
}

.plan-price {
  font-size: 1.3rem;
  font-weight: 800;
  color: #213547;
  letter-spacing: -0.02em;
}
.dark-theme .plan-price {
  color: white;
}
.plan-period {
  font-size: 0.75rem;
  font-weight: 500;
  color: #888;
}
.plan-sub {
  font-size: 0.7rem;
  color: #aaa;
  margin-top: 2px;
}

.plan-radio {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.6rem;
  transition: border-color 0.2s;
}
.plan-radio-selected {
  border-color: var(--color-accent-purple);
}
.plan-radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-accent-purple);
}

/* ── Stripe ── */
.paywall-stripe {
  margin-bottom: 0.5rem;
}

/* ── CTA button ── */
.paywall-cta {
  width: 100%;
  border: none;
  border-radius: var(--radius-pill);
  padding: 0.9rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  background: var(--gradient-primary);
  color: white;
  cursor: pointer;
  box-shadow: var(--shadow-purple);
  transition:
    opacity 0.15s,
    transform 0.15s;
  margin-bottom: 0.75rem;
}
.paywall-cta:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}
.paywall-cta:active {
  transform: scale(0.97);
}

/* ── Footer ── */
.paywall-footer {
  text-align: center;
}
.paywall-free-btn {
  background: none;
  border: none;
  font-family: 'Poppins', sans-serif;
  font-size: 0.82rem;
  color: #888;
  cursor: pointer;
  padding: 0.5rem;
  text-decoration: underline;
  margin-bottom: 0.75rem;
  transition: color 0.15s;
}
.paywall-free-btn:hover {
  color: #555;
}

.paywall-legal-links {
  display: flex;
  justify-content: center;
  gap: 1rem;
}
.legal-link {
  font-size: 0.72rem;
  color: #aaa;
  text-decoration: none;
  font-family: 'Poppins', sans-serif;
}
.legal-link:hover {
  color: #666;
  text-decoration: underline;
}

/* ── Success ── */
.paywall-success {
  text-align: center;
  padding: 3rem 1rem;
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.success-icon {
  font-size: 2.5rem;
  color: var(--color-accent-purple);
  margin-bottom: 1rem;
  animation: popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
.success-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.75rem;
  letter-spacing: -0.02em;
}
.dark-theme .success-title {
  color: white;
}
.success-body {
  font-size: 0.88rem;
  color: #515a65;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}
.dark-theme .success-body {
  color: #d4d2d2;
}

.plan-price-original {
  text-decoration: line-through;
  color: #aaa;
  font-size: 0.85rem;
  font-weight: 500;
  margin-right: 6px;
}
</style>

<!-- <script setup>
import { ref, onMounted, computed } from 'vue';
import StripePayment from '../../components/StripePayments.vue';
import appIcon from '@/assets/images/LuckyFridayBrightIcon.png';
import { useRouter, useRoute } from 'vue-router';
import ProgressBar from '../../components/ProgressBar.vue';
import { Capacitor } from '@capacitor/core';
import {
  RevenueCatUI,
  PAYWALL_RESULT,
} from '@revenuecat/purchases-capacitor-ui';
import { useMyStore } from '../../store';

const selectedPlan = ref('monthly');
const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const router = useRouter();
const route = useRoute();
const isExistingUser = route.query.isExistingUser;
const subscribed = ref(false);
const isIosApp = ref(false);
const store = useMyStore();
const premiumUser = computed(() => store.user.premiumSubscription);

const selectPlan = (planType) => {
  selectedPlan.value = planType;
};

const handlePaymentSuccess = async () => {
  subscribed.value = true;
};

const goToCategorySelection = () => {
  router.push({ name: 'UserCategoryData' });
};

const useFreeVersion = () => {
  if (isExistingUser) {
    router.push({ name: 'UserProfile' });
  } else {
    goToCategorySelection();
  }
};

const goToAccountPage = () => {
  router.push({ name: 'UserProfile' });
};

onMounted(() => {
  setTimeout(async () => {
    if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
      isIosApp.value = true;
      await presentIosPaywall();
      return;
    }

    const mainElement = document.querySelector('.main');
    if (mainElement) {
      mainElement.scrollTo(0, 0);
    }
  }, 100);
});

// onMounted(() => {
//   setTimeout(() => {
//     if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
//       isIosApp.value = true;
//       return;
//     }

//     const mainElement = document.querySelector('.main');
//     if (mainElement) {
//       mainElement.scrollTo(0, 0);
//     }
//   }, 100);
// });

const presentIosPaywall = async () => {
  try {
    const { result } = await RevenueCatUI.presentPaywall();
    switch (result) {
      case PAYWALL_RESULT.PURCHASED:
      case PAYWALL_RESULT.RESTORED:
        await store.activateIapSubscription();
        subscribed.value = true;
        break;
      default:
        break;
    }
  } catch (e) {
    console.error('Paywall error:', e);
  }
};
</script>

<template>
  <div class="subscription-container">
    <div class="flex justify-center">
      <img :src="appIcon" alt="App Icon" class="app-icon" />
    </div>
    <div v-if="!subscribed && !isIosApp">
      <div class="subscription-benefits">
        <ul class="benefits-list">
          <li>✅ 7-Day Free Trial</li>
          <li>✅ One-Click Cancel Anytime</li>
          <li>✅ No Ads — Ever</li>
          <li>✅ Full Feature Access During Trial</li>
          <li>✅ Data Privacy: We’ll never sell or share your info</li>
        </ul>
      </div>
      <div class="flex subscription-options">
        <div
          class="wrapper-card"
          :class="{ active: selectedPlan === 'monthly' }"
        >
          $<span class="cost">12</span>/<sub>mo</sub>
          <p><strong>Monthly</strong></p>
          <p class="cost-info">month-to-month flexibility</p>
          <button
            v-if="selectedPlan === 'monthly'"
            class="teal-button w-100"
            @click="selectPlan('monthly')"
          >
            Selected
          </button>
          <button
            v-else
            class="teal-outline-button w-100"
            @click="selectPlan('monthly')"
          >
            Select
          </button>
        </div>
        <div
          class="wrapper-card"
          :class="{ active: selectedPlan === 'annual' }"
        >
          $<span class="cost">9.60</span>/<sub>mo</sub>
          <p><strong>Yearly</strong></p>
          <p class="cost-info">
            pay $115.20 upfront and <strong>save 20%</strong>
          </p>
          <button
            v-if="selectedPlan === 'annual'"
            class="teal-button w-100"
            @click="selectPlan('annual')"
          >
            Selected
          </button>
          <button
            v-else
            class="teal-outline-button w-100"
            @click="selectPlan('annual')"
          >
            Select
          </button>
        </div>
      </div>
      <StripePayment
        :amount="amount"
        :publishable-key="publishableKey"
        @payment-success="handlePaymentSuccess"
        @apple-pay-available="handleApplePayStatus"
        :selectedPlan="selectedPlan"
      />
      <div>
        <p class="cost-info">
          You can skip bank linking and use Lucky Friday for free. Just enter
          your transactions manually — we’ll keep everything organized.
        </p>
        <button class="button free-button" @click="useFreeVersion">
          Use free version
        </button>
      </div>
    </div>
    <div v-if="!subscribed && isIosApp">
      <div class="subscription-benefits">
        <ul class="benefits-list">
          <li>✅ 7-Day Free Trial</li>
          <li>✅ One-Click Cancel Anytime</li>
          <li>✅ No Ads — Ever</li>
          <li>✅ Full Feature Access During Trial</li>
          <li>✅ Data Privacy: We'll never sell or share your info</li>
        </ul>
      </div>
      <button class="teal-button w-100" @click="presentIosPaywall">
        Upgrade to Premium
      </button>
      <div class="mt-2">
        <p class="cost-info">
          You can skip bank linking and use Lucky Friday for free. Just enter
          your transactions manually — we'll keep everything organized.
        </p>
        <button class="button free-button" @click="useFreeVersion">
          Use free version
        </button>
      </div>
    </div>
    <div v-else>
      <div v-if="isExistingUser" class="mt-1">
        <p v-if="premiumUser" class="text-center">
          You are now subscribed to the most customizable budgeting app on the
          market.
        </p>
        <button
          class="button teal-outline-button w-100"
          @click="goToAccountPage"
        >
          Go back to User Profile
        </button>
      </div>
      <div v-else>
        <p class="text-center">
          You are now subscribed to the most customizable budgeting app on the
          market. Let's get you started setting up your first categories.
        </p>
        <button
          class="button teal-outline-button w-100"
          @click="goToCategorySelection"
        >
          Let's go
        </button>
      </div>
    </div>
    <ProgressBar v-if="!isExistingUser" :currentStep="2" :steps="5" />
  </div>
</template>

<style scoped>
.subscription-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 1em;
}

.plan-selector {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

button {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

button.active {
  background-color: #4a6cf7;
  color: white;
  border-color: #4a6cf7;
}

.subscription-options {
  gap: 1em;
  margin-bottom: 4em;
}

.subscription-options .wrapper-card {
  padding: 1em;
  text-align: center;
}

.subscription-options .wrapper-card.active {
  box-shadow: 0px 5px 25px 0px rgb(12 158 42 / 30%);
}

.cost {
  font-weight: bold;
  font-size: 1.5em;
}

.light-theme .cost {
  color: #004751;
}

.dark-theme .cost {
  color: #137b89;
}

.cost-info {
  font-size: 0.8em;
  color: #747373;
}

.payment-form {
  margin-bottom: 4em;
}

.free-button {
  background: #f8f8f8;
  font-size: 0.8em;
  width: 100%;
  color: #5b5b5b;
}

.subscription-benefits {
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
  text-align: left;
  font-size: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.light-theme .subscription-benefits {
  background-color: #f8f8f8;
}

.dark-theme .subscription-benefits {
  background-color: #212e3e;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dark-theme .benefits-list {
  background-color: #212e3e;
}

.light-theme .benefits-list li {
  margin-bottom: 10px;
  color: #213547;
}

.dark-theme .benefits-list li {
  margin-bottom: 10px;
  color: #fff;
}

.icon {
  text-align: center;
}

.app-icon {
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  display: block;
}
</style> -->
